import React, { memo, MouseEvent, useMemo } from "react";
import styled from "styled-components";
import { ChevronRight } from "lucide-react";
import { LanguageNameSpaces, Signal } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import { formatDetailDate } from "common/NewLook/formatDetailDate.ts";
import { ICON_TYPES } from "@shared/common/constants";
import IconWithFallback from "Global/IconWithFallback";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import AutoSizeText from "common/Text/AutoSizeText.tsx";
import {
  getSignalDetails,
  getSignalExchanges,
  getSignalFollowNote,
  PhoneSignalDetail,
  PhoneSignalDetailKey,
  PhoneSignalFollowState,
} from "features/signals/NewLook/phoneSignalCardFigures.ts";
import {
  SIGNAL_CARD_DETAIL_ID,
  SIGNAL_CARD_DETAILS_BUTTON_ID,
  SIGNAL_CARD_DETAILS_ID,
  SIGNAL_CARD_EXCHANGE_ID,
} from "testIds";

const VALUE_MIN_FONT_SIZE = 10;
const EXCHANGE_ICON_SIZE = 16;
const DETAIL_LABELS: Record<
  PhoneSignalDetailKey,
  TranslationKeys<LanguageNameSpaces.SIGNALS>
> = {
  opened: "phone.detail_opened",
  updated: "phone.detail_updated",
  closed: "phone.detail_closed",
};
const FOLLOW_NOTES: Record<
  PhoneSignalFollowState,
  TranslationKeys<LanguageNameSpaces.SIGNALS>
> = {
  following: "follow.messages.open_trade_exists",
  update: "follow.messages.signal_updated",
  cancel: "follow.messages.signal_canceled",
};

interface PhoneSignalDetailsProps {
  signal: Signal;
  onOpenDetails?: () => void;
}

const PhoneSignalDetails = ({
  signal,
  onOpenDetails,
}: PhoneSignalDetailsProps) => {
  const details = useMemo(
    () => getSignalDetails(signal, formatDetailDate),
    [signal]
  );
  const followState = getSignalFollowNote(signal);
  return (
    <Details data-testid={SIGNAL_CARD_DETAILS_ID}>
      {!!details.length && (
        <Grid>
          {details.map((detail) => (
            <DetailCell key={detail.key} detail={detail} />
          ))}
        </Grid>
      )}
      <Exchanges>
        {getSignalExchanges(signal).map((exchange) => (
          <ExchangeLine key={exchange} exchange={exchange} />
        ))}
      </Exchanges>
      <Bottom>
        {followState && <FollowNote state={followState} />}
        {onOpenDetails && <OpenDetailsLink onOpen={onOpenDetails} />}
      </Bottom>
    </Details>
  );
};

export default memo(PhoneSignalDetails);

const DetailCell = ({ detail }: { detail: PhoneSignalDetail }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  return (
    <Cell data-testid={SIGNAL_CARD_DETAIL_ID} data-key={detail.key}>
      <CellLabel>{t(DETAIL_LABELS[detail.key])}</CellLabel>
      <CellValue title={detail.value}>
        <AutoSizeText inheritFont={true} minFontSizePx={VALUE_MIN_FONT_SIZE}>
          {detail.value}
        </AutoSizeText>
      </CellValue>
    </Cell>
  );
};

const ExchangeLine = ({ exchange }: { exchange: string }) => (
  <Line data-testid={SIGNAL_CARD_EXCHANGE_ID}>
    <IconWithFallback
      coinOrExchangeName={exchange}
      iconType={ICON_TYPES.EXCHANGE}
      pxIconSize={EXCHANGE_ICON_SIZE}
    />
    <LineText>{exchange}</LineText>
  </Line>
);

const FollowNote = ({ state }: { state: PhoneSignalFollowState }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  return (
    <Note data-state={state} $state={state}>
      {t(FOLLOW_NOTES[state])}
    </Note>
  );
};

const OpenDetailsLink = ({ onOpen }: { onOpen: () => void }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const onClick = (event: MouseEvent) => {
    event.stopPropagation();
    onOpen();
  };
  return (
    <OpenLink
      type="button"
      onClick={onClick}
      data-testid={SIGNAL_CARD_DETAILS_BUTTON_ID}
    >
      {t("drawer.signal_details")}
      <ChevronRight size={14} strokeWidth={2} />
    </OpenLink>
  );
};

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 12px;
`;
const Cell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;
const CellLabel = styled.span`
  font-size: 11px;
  line-height: 14px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  white-space: nowrap;
`;
const CellValue = styled.div`
  min-width: 0;
  font-size: 13px;
  line-height: 17px;
  font-weight: 450;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Exchanges = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 12px;
  &:empty {
    display: none;
  }
`;
const Line = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 13px;
  line-height: 17px;
  font-weight: 450;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const LineText = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
`;
const Bottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 20px;
`;
const Note = styled.span<{ $state: PhoneSignalFollowState }>`
  min-width: 0;
  font-size: 11.5px;
  line-height: 15px;
  color: ${(p) =>
    p.$state === "following"
      ? getNewLookTokens(p.theme).muted
      : p.$state === "cancel"
      ? p.theme.colors.red
      : p.theme.colors.gold};
  overflow-wrap: anywhere;
`;
const OpenLink = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin: -8px -4px -8px auto;
  padding: 8px 4px;
  border: 0;
  background: none;
  font: inherit;
  font-size: 12.5px;
  line-height: 16px;
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).accent};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;
